import { useEffect, useMemo, useRef, useState } from 'react';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import { callingCodeOf } from 'components/CallScribe/Home/dialFormat';
import { getContactByPhoneNumber } from 'services/pod';
import { getDialSuggestions, IDialSuggestion } from 'services/callScribe/dialSuggestions';

export const MIN_SUGGEST_DIGITS = 2;
export const MAX_SUGGESTIONS = 5;
const SUGGEST_DEBOUNCE_MS = 250;
const LOOKUP_DEBOUNCE_MS = 600;

const digitsOf = (value: string): string => value.replace(/\D+/g, '');

const knownIn = (cache: Map<string, IDialSuggestion[]>, scope: string): IDialSuggestion[] => {
  const seen = new Set<string>();
  const known: IDialSuggestion[] = [];
  cache.forEach((rows, key) => {
    if (!key.startsWith(scope)) return;
    rows.forEach(row => {
      if (seen.has(row.phoneE164)) return;
      seen.add(row.phoneE164);
      known.push(row);
    });
  });
  return known;
};

export const useDialSuggestions = (
  crmSourceId: number | null | undefined,
  country: CountryCode,
  national: string,
): IDialSuggestion[] => {
  const code = callingCodeOf(country);
  const prefix = `${code}${national}`;
  const active = crmSourceId != null && national.length >= MIN_SUGGEST_DIGITS;

  const [fetched, setFetched] = useState<IDialSuggestion[]>([]);
  const [exact, setExact] = useState<IDialSuggestion | null>(null);
  const requestRef = useRef(0);
  const lookupRef = useRef(0);
  const cacheRef = useRef(new Map<string, IDialSuggestion[]>());
  const lookupCacheRef = useRef(new Map<string, IDialSuggestion | null>());

  useEffect(() => {
    const reqId = ++requestRef.current;
    if (!active) {
      setFetched([]);
      return undefined;
    }
    const scope = `${crmSourceId}:${code}:`;
    const key = `${scope}${national}`;
    const cached = cacheRef.current.get(key);
    if (cached) {
      setFetched(cached);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void (async () => {
        let rows: IDialSuggestion[] | null = null;
        try {
          const res = await getDialSuggestions({
            crmSourceId,
            countryCode: code,
            digits: national,
            limit: MAX_SUGGESTIONS,
          });
          rows = Array.isArray(res?.data) ? res.data.slice(0, MAX_SUGGESTIONS) : null;
        } catch {
          rows = null;
        }
        if (reqId !== requestRef.current) return;
        if (rows) cacheRef.current.set(key, rows);
        setFetched(rows ?? knownIn(cacheRef.current, scope));
      })();
    }, SUGGEST_DEBOUNCE_MS);
    return () => { window.clearTimeout(timer); };
  }, [active, crmSourceId, code, national]);

  const parsed = useMemo(
    () => (active ? parsePhoneNumberFromString(national, country) : undefined),
    [active, national, country],
  );
  const e164 = parsed?.isValid() ? parsed.number : '';
  const matched = useMemo(
    () => fetched.some(s => s.phoneE164 === e164),
    [fetched, e164],
  );

  useEffect(() => {
    const reqId = ++lookupRef.current;
    if (!e164 || crmSourceId == null || matched) {
      setExact(null);
      return undefined;
    }
    const cached = lookupCacheRef.current.get(e164);
    if (cached !== undefined) {
      setExact(cached);
      return undefined;
    }
    setExact(null);
    const timer = window.setTimeout(() => {
      void (async () => {
        let found: IDialSuggestion | null = null;
        let answered = false;
        try {
          const res = await getContactByPhoneNumber({ crmId: crmSourceId, phone: e164 });
          const contact = res?.data;
          answered = true;
          if (contact?.userId && !contact.isAmbiguous) {
            found = {
              name: contact.userName,
              phoneE164: e164,
              contactExternalId: String(contact.userId),
              companyExternalId: contact.companyId ? String(contact.companyId) : null,
              crmSourceId,
              source: 'psa',
            };
          }
        } catch {
          found = null;
        }
        if (reqId !== lookupRef.current) return;
        if (answered) lookupCacheRef.current.set(e164, found);
        setExact(found);
      })();
    }, LOOKUP_DEBOUNCE_MS);
    return () => { window.clearTimeout(timer); };
  }, [e164, crmSourceId, matched]);

  return useMemo(() => {
    if (!active) return [];
    const current = fetched.filter(s => digitsOf(s.phoneE164).startsWith(prefix));
    const withExact = exact && exact.phoneE164 === e164 ? [exact, ...current] : current;
    return withExact.slice(0, MAX_SUGGESTIONS);
  }, [active, fetched, exact, e164, prefix]);
};
